import React, { useMemo, useState } from 'react';
import {
  Award, UserCheck, ClipboardList, BookOpen, ShieldCheck, Scale, FileCheck,
  Search, FileText, X
} from 'lucide-react';
import { Card, CardBody, CardHeader } from '@/shared/components/ui/Card';
import { type BadgeTone } from '@/shared/components/ui/Badge';
import { Skeleton } from '@/shared/components/ui/Skeleton';
import { type CatalogEntry, type HrDocType, type CustomTemplateSummary } from '../api';

const DOC_TYPE_META: Record<HrDocType, { subtitle: string; icon: React.ElementType; badgeTone: BadgeTone }> = {
  offer_letter: {
    subtitle: 'Formal compensation & role offer',
    icon: Award,
    badgeTone: 'primary',
  },
  appointment_letter: {
    subtitle: 'Official employment contract',
    icon: UserCheck,
    badgeTone: 'success',
  },
  enrolment_form: {
    subtitle: 'Employee data collection sheet',
    icon: ClipboardList,
    badgeTone: 'warning',
  },
  induction: {
    subtitle: 'Policy & handbook sign-off',
    icon: BookOpen,
    badgeTone: 'info',
  },
  nda: {
    subtitle: 'Non-Disclosure Agreement',
    icon: ShieldCheck,
    badgeTone: 'danger',
  },
  code_of_conduct: {
    subtitle: 'Workplace ethics & compliance',
    icon: Scale,
    badgeTone: 'primary',
  },
  relieving_letter: {
    subtitle: 'Service tenure certificate',
    icon: FileCheck,
    badgeTone: 'neutral',
  },
};

type StatusFilter = 'all' | 'published' | 'draft' | 'standard';

interface TemplateSidebarProps {
  catalog: CatalogEntry[] | undefined;
  isLoading: boolean;
  isError: boolean;
  selected: HrDocType | null;
  onSelect: (docType: HrDocType) => void;
  department: string;
}

export function TemplateSidebar({
  catalog,
  isLoading,
  isError,
  selected,
  onSelect,
  department,
}: TemplateSidebarProps) {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

  function getStatus(summary: CustomTemplateSummary | undefined) {
    if (!summary) return { label: 'Standard', status: 'standard', tone: 'neutral' as BadgeTone };
    if (summary.status === 'published') return { label: 'Published', status: 'published', tone: 'success' as BadgeTone };
    return { label: 'Draft', status: 'draft', tone: 'warning' as BadgeTone };
  }

  const filteredCatalog = useMemo(() => {
    if (!catalog) return [];
    return catalog.filter((item) => {
      const summary = item.customTemplates.find((t) => (t.department || '') === department);
      const st = getStatus(summary).status;
      if (statusFilter !== 'all' && st !== statusFilter) return false;

      if (search.trim()) {
        const q = search.toLowerCase();
        const labelMatch = item.label.toLowerCase().includes(q);
        const metaMatch = DOC_TYPE_META[item.docType]?.subtitle.toLowerCase().includes(q) || false;
        return labelMatch || metaMatch;
      }
      return true;
    });
  }, [catalog, department, search, statusFilter]);

  return (
    <Card className="h-full flex flex-col shadow-sm border-outline">
      <CardHeader
        title="Document Catalog"
        subtitle="Select a template to configure"
      />
      
      {/* Search & Filter Bar */}
      <div className="px-3 pb-3 space-y-2 border-b border-outline/50">
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search templates..."
            className="w-full h-8 rounded-md border border-outline bg-surface-variant/40 pl-8 pr-7 text-xs text-on-surface placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              className="absolute right-2 top-2 text-muted-foreground hover:text-on-surface"
              aria-label="Clear search"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Filter Pills */}
        <div className="flex items-center gap-1 overflow-x-auto pb-0.5">
          {(['all', 'published', 'draft', 'standard'] as StatusFilter[]).map((f) => {
            const active = statusFilter === f;
            const labels: Record<StatusFilter, string> = {
              all: 'All',
              published: 'Published',
              draft: 'Drafts',
              standard: 'Standard',
            };
            return (
              <button
                key={f}
                type="button"
                onClick={() => setStatusFilter(f)}
                className={`rounded-full px-2.5 py-0.5 text-[11px] font-medium transition-colors whitespace-nowrap ${
                  active
                    ? 'bg-primary text-on-primary shadow-xs'
                    : 'bg-surface-variant/70 text-on-surface-variant hover:bg-surface-variant'
                }`}
              >
                {labels[f]}
              </button>
            );
          })}
        </div>
      </div>

      <CardBody className="p-2 flex-1 overflow-y-auto max-h-[640px]">
        {isLoading ? (
          <div className="p-3 space-y-3">
            {[...Array(6)].map((_, i) => (
              <Skeleton key={i} className="h-16 w-full rounded-lg" />
            ))}
          </div>
        ) : isError ? (
          <div className="p-4 text-center text-sm text-danger-text">
            Failed to load document templates catalog.
          </div>
        ) : filteredCatalog.length === 0 ? (
          <div className="py-12 px-4 text-center">
            <FileText className="h-8 w-8 mx-auto text-muted-foreground/40 mb-2" />
            <p className="text-xs font-medium text-on-surface">No matching templates</p>
            <p className="text-[11px] text-muted-foreground mt-0.5">Try clearing your search or filters.</p>
          </div>
        ) : (
          <ul className="space-y-1.5">
            {filteredCatalog.map((d) => {
              const summary = d.customTemplates.find((t) => (t.department || '') === department);
              const statusInfo = getStatus(summary);
              const meta = DOC_TYPE_META[d.docType] || {
                subtitle: 'HR legal document',
                icon: FileText,
                badgeTone: 'neutral',
              };
              const Icon = meta.icon;
              const active = d.docType === selected;

              return (
                <li key={d.docType}>
                  <button
                    type="button"
                    onClick={() => onSelect(d.docType)}
                    className={`group relative w-full flex items-start gap-3 rounded-xl p-3 text-left transition-all duration-150 border ${
                      active
                        ? 'bg-primary/10 border-primary/40 shadow-sm text-primary'
                        : 'bg-surface hover:bg-surface-variant/50 border-transparent hover:border-outline/50 text-on-surface'
                    }`}
                    aria-current={active}
                  >
                    {/* Active left indicator bar */}
                    {active && (
                      <span className="absolute left-0 top-3 bottom-3 w-1 rounded-r-full bg-primary" />
                    )}

                    {/* Icon Box */}
                    <div
                      className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border transition-colors ${
                        active
                          ? 'bg-primary text-on-primary border-primary'
                          : 'bg-surface-variant/70 text-on-surface-variant border-outline/30 group-hover:bg-surface-variant'
                      }`}
                    >
                      <Icon className="h-4 w-4" />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className={`text-xs font-semibold truncate ${active ? 'text-primary' : 'text-on-surface'}`}>
                          {d.label}
                        </span>
                        {/* Status indicator badge */}
                        <span
                          className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-medium border ${
                            statusInfo.status === 'published'
                              ? 'bg-success/10 text-success-text border-success/30'
                              : statusInfo.status === 'draft'
                              ? 'bg-warning/10 text-warning-text border-warning/30'
                              : 'bg-surface-variant text-muted-foreground border-outline/40'
                          }`}
                        >
                          <span
                            className={`h-1.5 w-1.5 rounded-full ${
                              statusInfo.status === 'published'
                                ? 'bg-success'
                                : statusInfo.status === 'draft'
                                ? 'bg-warning'
                                : 'bg-slate-400'
                            }`}
                          />
                          {statusInfo.label}
                        </span>
                      </div>

                      <p className="text-[11px] text-muted-foreground truncate mt-0.5">
                        {meta.subtitle}
                      </p>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </CardBody>
    </Card>
  );
}
